import { Zap } from 'lucide-react';

type StepId = 1 | 2;

interface OnboardingIllustrationProps {
  step: StepId;
  caption: string;
}

/**
 * Panel izquierdo del OnboardingModal: mockup animado de una factura
 * flotando, con badges contextuales según el paso activo.
 *
 * Paso 1 -> insignia "Listo en segundos" debajo de la tarjeta.
 * Paso 2 -> recuadro resaltado sobre la tarjeta + tooltip tipo "tour guiado".
 */
export function OnboardingIllustration({ step, caption }: OnboardingIllustrationProps) {
  return (
    <div className="relative flex min-h-0 flex-col overflow-hidden border-r border-muted bg-[#E3EFFF] p-6">
      {/* Fondo y círculos decorativos: color fijo a propósito (no tokens de tema) — deben verse igual en claro y oscuro */}
      <div className="absolute -top-[72px] -right-[84px] h-60 w-60 rounded-full bg-[rgba(22,98,197,0.12)] blur-[6px]" />
      <div className="absolute -bottom-[52px] -left-[44px] h-40 w-40 rounded-full bg-[rgba(22,98,197,0.09)] blur-[6px]" />

      {/* Indicador "Paso X de 2" */}
      <div className="relative z-10 flex items-center gap-2 text-caption font-medium text-brand-hover">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand" />
        Paso <span className="ml-0.5">{step} de 2</span>
      </div>

      {/* Contenedor del mockup flotante */}
      <div
        className={`relative z-10 flex flex-1 min-h-0 items-center justify-center overflow-hidden transition-[padding] duration-500 ease-[cubic-bezier(.32,.72,0,1)] ${
          step === 1 ? 'py-10 pb-16' : 'py-4 pb-[116px]'
        }`}
      >
        <div className="relative w-[180px] flex-none">
          {/* Sombra flotante */}
          <span
            className="absolute left-1/2 top-full mt-1 h-[11px] w-[124px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(16,24,40,.42),transparent_72%)] blur-[5px] motion-safe:animate-onboarding-float-shadow motion-reduce:animate-none"
          />

          {/* Tarjeta mockup (factura) */}
          <div className="relative w-[180px] rounded-2xl border border-[#CBD5E1] bg-white p-4 shadow-[0_8px_22px_rgba(16,24,40,.10)] motion-safe:animate-onboarding-float motion-reduce:animate-none will-change-transform">
            <div className="mb-3 flex items-center gap-2">
              <span className="grid h-6 w-6 flex-none place-items-center rounded-[7px] bg-[#1662C5] font-display text-[12px] font-bold text-white">
                S
              </span>
              <span className="flex-1">
                <span className="block h-[5px] w-[70%] rounded-[3px] bg-[#F1F5F9]" />
                <span className="mt-1 block h-1 w-[45%] rounded-[3px] bg-[#F1F5F9]" />
              </span>
            </div>

            <div className="mb-3 h-px bg-[#E2E8F0]" />

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="h-1 flex-1 rounded-[3px] bg-[#F1F5F9]" />
                <span className="h-1 w-7 rounded-[3px] bg-[rgba(22,98,197,0.8)]" />
              </div>
              <div className="flex items-center gap-2">
                <span className="h-1 flex-1 rounded-[3px] bg-[#F1F5F9]" />
                <span className="h-1 w-7 rounded-[3px] bg-[#F1F5F9]" />
              </div>
              <div className="flex items-center gap-2">
                <span className="h-1 flex-1 rounded-[3px] bg-[#F1F5F9]" />
                <span className="h-1 w-7 rounded-[3px] bg-[#F1F5F9]" />
              </div>
            </div>

            <div className="my-3 h-px bg-[#E2E8F0]" />

            <div className="flex items-end justify-between">
              <span className="block h-9 w-9 rounded-md border border-[#E2E8F0] bg-[#F1F5F9]" />
              <span className="block">
                <span className="mb-1.5 block h-1 w-10 rounded-[3px] bg-[#F1F5F9]" />
                <span className="block h-2.5 w-[58px] rounded-[3px] bg-[#1662C5]" />
              </span>
            </div>

            {/* Badge paso 1: "Listo en segundos" */}
            {step === 1 && (
              <span className="absolute left-0 right-0 top-full mx-auto mt-3 flex h-[27px] w-fit items-center gap-1.5 whitespace-nowrap rounded-full bg-[#E3EFFF] px-3 text-caption font-semibold leading-none text-[#1456AD] shadow-[0_4px_14px_rgba(16,24,40,.12)] animate-fadeIn">
                <Zap className="h-3 w-3" strokeWidth={2} />
                Listo en segundos
              </span>
            )}

            {/* Highlight + tooltip paso 2: simula el tour guiado */}
            {step === 2 && (
              <>
                <span className="absolute left-1.5 right-1.5 top-[58px] h-[62px] rounded-[9px] border-[1.5px] border-[#1662C5] bg-[rgba(22,98,197,0.07)] shadow-[0_0_0_4px_rgba(22,98,197,.14)] animate-fadeIn" />
                <span className="absolute -left-[9px] top-12 grid h-[22px] w-[22px] place-items-center rounded-full bg-[#1662C5] font-display text-[11px] font-bold leading-none text-white shadow-[0_3px_10px_rgba(16,24,40,.24)] animate-fadeIn">
                  2
                </span>

                <span className="absolute left-1/2 top-full mt-3 box-border w-[214px] -translate-x-1/2 rounded-xl bg-neutral-100 p-3 text-white shadow-[0_10px_28px_rgba(16,24,40,.28)] animate-fadeIn">
                  <span className="absolute -top-[5px] left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 rounded-sm bg-neutral-100" />
                  <span className="relative mb-1.5 block text-[10.5px] font-semibold uppercase tracking-[.09em] leading-none text-white/50">
                    Paso 2 de 4
                  </span>
                  <span className="relative mb-2.5 block font-display text-[12.5px] font-semibold leading-snug">
                    Agrega los productos de tu venta
                  </span>
                  <span className="relative flex items-center gap-2.5">
                    <span className="flex items-center gap-1">
                      <span className="h-[5px] w-[5px] rounded-full bg-white/[0.34]" />
                      <span className="h-[5px] w-3.5 rounded-full bg-white" />
                      <span className="h-[5px] w-[5px] rounded-full bg-white/[0.34]" />
                      <span className="h-[5px] w-[5px] rounded-full bg-white/[0.34]" />
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 font-display text-[11.5px] font-semibold leading-none text-neutral-100">
                      Siguiente
                    </span>
                  </span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <p className="relative z-10 m-0 flex-none text-body-sm leading-relaxed text-secondary">{caption}</p>
    </div>
  );
}
